import { IconButton, Skeleton } from 'components/ui'
import { useTheme } from 'next-themes'
import { useRouter } from 'next/router'
import { cn } from 'utils/cn'
import { useCallback, useEffect, useState } from 'react'
import {
  Dashboard,
  Building,
  UserMultiple,
  User,
  Catalog,
  Activity,
  Meter,
  Money,
  Events,
  Flag,
  UserAvatar,
  Menu as MenuIcon,
  Close,
} from '@carbon/icons-react'
import { NextPaths } from 'router/next-paths'
import { useAuthState } from 'contexts/AuthContext'

import { ImpersonationProvider } from 'contexts/ImpersonationContext'
import { ProviderSelectorProvider } from 'contexts/ProviderSelectorContext'
import { OperationEnvironmentProvider } from 'contexts/OperationEnvironmentContext'
import ImpersonationBanner from 'components/operation/ImpersonationBanner'
import ProviderSelector from '../components/operation/ProviderSelector'
import OperationEnvironmentToggle from '../components/operation/OperationEnvironmentToggle'
import Sidebar from 'components/shared/Sidebar'
import ThemeToggle from 'components/shared/ThemeToggle'
import { useMenuPreferences } from 'hooks/useMenuPreferences'
import { SocketQueryBridge, operationRules } from 'lib/socket-query-bridge'

const navItems = [
  { label: 'Dashboard', path: NextPaths.superAdminDashboard, icon: Dashboard },
  { label: 'Providers', path: NextPaths.superAdminProviders, icon: Building },
  { label: 'Users', path: NextPaths.superAdminUsers, icon: UserMultiple },
  { label: 'Customers', path: NextPaths.superAdminCustomers, icon: User },
  { label: 'Transactions', path: NextPaths.superAdminTransactions, icon: Money },
  { label: 'Events', path: NextPaths.superAdminEvents, icon: Events },
  { label: 'Usage', path: NextPaths.superAdminUsage, icon: Meter },
  { label: 'Preregistrations', path: NextPaths.superAdminPreregistrations, icon: Catalog },
  { label: 'Audit log', path: NextPaths.superAdminAuditLog, icon: Activity },
  { label: 'Feature flags', path: NextPaths.superAdminFeatureFlags, icon: Flag },
]
const bottomItems = [{ label: 'Account', path: NextPaths.superAdminAccount, icon: UserAvatar }]

interface OperationLayoutProps {
  children: React.ReactNode
}

const OperationLayoutContent: React.FC<OperationLayoutProps> = ({ children }) => {
  const router = useRouter()
  const { isCollapsed } = useMenuPreferences()
  const menuWidth = isCollapsed ? 70 : 200
  const { resolvedTheme } = useTheme()
  const isDark = resolvedTheme === 'dark'
  const logoSrc = isDark ? '/logo-white.svg' : '/logo-black.svg'
  const iconSrc = isDark ? '/icon-white.svg' : '/icon-black.svg'

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [mobileMenuClosing, setMobileMenuClosing] = useState(false)
  const onDrawerOpen = useCallback(() => setMobileMenuOpen(true), [])
  const onDrawerClose = useCallback(() => {
    setMobileMenuClosing(true)
    setTimeout(() => {
      setMobileMenuOpen(false)
      setMobileMenuClosing(false)
    }, 200)
  }, [])

  const menuToggleButtonClass =
    'h-8 w-8 min-w-8 rounded-full border border-line-subtle bg-surface-muted p-0 text-content-secondary hover:bg-surface-subtle hover:text-content-primary active:bg-surface-subtle'

  const renderTopNav = (collapsed: boolean) => (
    <div className={cn('py-2', collapsed ? 'px-2' : 'px-3')}>
      <div className="mb-3">
        <OperationEnvironmentToggle collapsed={collapsed} />
      </div>
      <ProviderSelector collapsed={collapsed} />
    </div>
  )

  const auth = useAuthState()

  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated) {
      router.push(NextPaths.superAdminLogin)
      return
    }

    if (!auth.isLoading && auth.isAuthenticated && !auth.user?.isSuperAdmin) {
      router.push(NextPaths.login)
      return
    }
  }, [auth.isAuthenticated, auth.isLoading, auth.user?.isSuperAdmin, router])

  if (auth.isLoading || !auth.isAuthenticated || !auth.user?.isSuperAdmin) {
    return (
      <div className={cn('flex', 'min-h-screen')}>
        <div className="fixed left-0 top-0 z-10 hidden h-screen min-h-screen w-[220px] flex-col border-r border-line-subtle bg-surface-subtle px-4 py-8 md:flex">
          <div className="mb-4 shrink-0 text-center">
            <Skeleton className="mx-auto mb-2 h-[40px] w-[150px]" />
          </div>
          { }
          <div className={cn('flex flex-col items-center', 'flex-1 items-stretch gap-1')}>
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={`sa-skel-${i}`} className="flex items-center px-4 py-3">
                <Skeleton className="mr-3 h-[20px] w-[20px]" />
                <Skeleton className="h-[16px] w-[120px]" />
              </div>
            ))}
          </div>
          { }
        </div>
        <div className="ml-0 min-h-screen flex-1 bg-surface-canvas md:ml-[220px]">
          <div className="max-w-[1200px] px-4 py-8 md:px-8">
            <div className={cn('flex flex-col items-center', 'items-stretch gap-6')}>
              <Skeleton className="h-[40px] w-[200px]" />
              <Skeleton className="h-[200px]" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={cn('flex', 'min-h-screen flex-col')}>
      <ImpersonationBanner />
      <div className={cn('flex', 'flex-1')}>
        <SocketQueryBridge rules={operationRules} />

        {/* Desktop sidebar */}
        <div className="hidden md:block">
          <Sidebar
            navItems={navItems}
            bottomItems={bottomItems}
            logoLink={NextPaths.superAdminDashboard}
            logoSrc={logoSrc}
            iconSrc={iconSrc}
            topNavContent={renderTopNav(isCollapsed)}
            useMenuPreferencesHook={true}
          />
        </div>

        {/* Mobile sidebar overlay */}
        {mobileMenuOpen && (
          <div className="fixed inset-0 z-[1400] md:hidden" onClick={onDrawerClose}>
            <div
              className={cn(
                'absolute inset-0 bg-black/60 transition-opacity duration-200 ease-in-out',
                mobileMenuClosing ? 'opacity-0' : 'opacity-100',
              )}
            />
            <div
              className={cn(
                'absolute bottom-0 left-0 top-0 w-[260px] bg-surface-subtle shadow-lg transition-transform duration-200 ease-in-out',
                !mobileMenuClosing && 'animate-in slide-in-from-left',
              )}
              style={{ transform: mobileMenuClosing ? 'translateX(-100%)' : 'translateX(0)' }}
              onClick={e => e.stopPropagation()}
            >
              <Sidebar
                navItems={navItems}
                bottomItems={bottomItems}
                logoLink={NextPaths.superAdminDashboard}
                logoSrc={logoSrc}
                iconSrc={iconSrc}
                headerContent={
                  <div className={cn('flex flex-row items-center', 'mt-2 justify-between')}>
                    <div className="flex h-8 w-8 items-center justify-center rounded-full border border-line-subtle bg-surface-muted">
                      <ThemeToggle />
                    </div>
                    <IconButton
                      aria-label="Close menu"
                      icon={<Close size={16} />}
                      onClick={onDrawerClose}
                      className={menuToggleButtonClass}
                    />
                  </div>
                }
                topNavContent={renderTopNav(false)}
                useMenuPreferencesHook={false}
                isCollapsed={false}
                isDrawerMode
                onNavigate={onDrawerClose}
              />
            </div>
          </div>
        )}

        {/* Main content */}
        <div
          className="relative ml-0 flex min-h-screen flex-1 flex-col justify-start overflow-x-hidden bg-surface-canvas md:ml-[var(--menu-w)]"
          style={
            {
              '--menu-w': `${menuWidth}px`,
              transition: 'margin-left 0.3s ease-in-out',
            } as React.CSSProperties
          }
        >
          {/* Mobile top bar */}
          <div
            className={cn(
              'flex flex-row items-center',
              'sticky top-0 z-[9] flex justify-between border-b border-line-subtle bg-surface-subtle px-4 py-3 md:hidden',
            )}
          >
            <IconButton
              aria-label="Open menu"
              icon={<MenuIcon size={20} />}
              onClick={onDrawerOpen}
              variant="ghost"
              size="sm"
              className="text-content-primary"
            />
            <img src={logoSrc} className="h-5" alt="SolvaPay" />
            <div className="w-8" />
          </div>

          <div className="w-full max-w-full px-4 py-4 md:max-w-[90%] md:px-8 md:py-8 lg:max-w-[1200px]">
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}

const OperationLayout: React.FC<OperationLayoutProps> = ({ children }) => {
  return (
    <OperationEnvironmentProvider>
      <ImpersonationProvider>
        <ProviderSelectorProvider>
          <OperationLayoutContent>{children}</OperationLayoutContent>
        </ProviderSelectorProvider>
      </ImpersonationProvider>
    </OperationEnvironmentProvider>
  )
}

export default OperationLayout
